import { getZendeskHelpCenterToken } from "src/apiService/modules/zendesk";
import { defaultDarkPalette, defaultLightPalette } from "src/theme";

// Public, host-mapped Help Center domain that users browse and land on.
export const ZENDESK_BASE_URL = "https://support.myresellergenie.com";

// JWT SSO hand-off must always target the Zendesk subdomain, even when host
// mapping is enabled. Per Zendesk: "If you have host mapping in place, you must
// send the JWT payload to your Zendesk Support subdomain ... not the
// host-mapped domain." The `return_to` below still points at ZENDESK_BASE_URL.
const ZENDESK_SSO_URL = "https://myresellergenie.zendesk.com";

// Detects whether the app is currently in dark mode by reading the body's
// background color (set from the active MUI theme via `CssBaseline`). This keeps
// the loading screen decoupled from React/Redux so the util needs no extra args.
function isAppInDarkMode(): boolean {
  const bg = window.getComputedStyle(document.body).backgroundColor;
  const match = bg.match(/\d+(\.\d+)?/g);
  if (!match || match.length < 3) return false;
  const [r, g, b] = match.map(Number);
  // Relative luminance (perceptual); < 0.5 means a dark background.
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 < 0.5;
}

// Lightweight "Loading…" page shown in the new tab while the SSO token
// resolves. We write this into the blank tab's document rather than opening a
// `data:` URL, because Chrome/Edge block content-initiated top-frame
// navigations to `data:` URLs (used for phishing) and would render about:blank.
// Colors follow the app's active theme (light/dark) and Open Sans typeface.
function buildLoadingHtml(): string {
  const isDark = isAppInDarkMode();
  const palette = isDark ? defaultDarkPalette : defaultLightPalette;
  const accent = isDark
    ? defaultDarkPalette.accent
    : defaultLightPalette.brandPrimary;
  return `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>My Reseller Genie</title>
    <link rel="preconnect" href="https://fonts.googleapis.com" />
    <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
    <link
      href="https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;600&display=swap"
      rel="stylesheet"
    />
    <style>
      html, body { height: 100%; margin: 0; }
      body {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 24px;
        background: ${palette.bg};
        color: ${palette.text};
        font-family: "Open Sans", "Helvetica", "Arial", sans-serif;
        -webkit-font-smoothing: antialiased;
      }
      .spinner {
        width: 56px;
        height: 56px;
        border: 5px solid ${palette.border};
        border-top-color: ${accent};
        border-radius: 50%;
        animation: spin 0.8s linear infinite;
      }
      .message {
        font-size: 22px;
        font-weight: 600;
        letter-spacing: 0.01em;
        opacity: 0.92;
      }
      @keyframes spin { to { transform: rotate(360deg); } }
      @media (prefers-reduced-motion: reduce) {
        .spinner { animation: none; }
      }
    </style>
  </head>
  <body>
    <div class="spinner"></div>
    <div class="message">Loading the Help Center…</div>
  </body>
</html>`;
}

/**
 * Opens a Zendesk URL with the user already authenticated via Help Center JWT
 * SSO. The new tab is opened synchronously inside the click gesture (to avoid
 * popup blockers) and shows a loading screen, then redirected to the SSO
 * endpoint once the short-lived, single-use token resolves. On any failure it
 * falls back to the plain URL.
 */
export function openZendeskWithSSO(returnToUrl: string) {
  const win = window.open("about:blank", "_blank");
  if (win) {
    win.focus();
    win.document.write(buildLoadingHtml());
    win.document.close();
  }
  getZendeskHelpCenterToken()
    .then((jwt) => {
      const url =
        `${ZENDESK_SSO_URL}/access/jwt?jwt=${encodeURIComponent(jwt)}` +
        `&return_to=${encodeURIComponent(returnToUrl)}`;
      if (win) win.location.href = url;
      else window.open(url, "_blank")?.focus();
    })
    .catch((e) => {
      console.error("Zendesk SSO", e);
      if (win) win.location.href = returnToUrl;
      else window.open(returnToUrl, "_blank")?.focus();
    });
}
